import React, { useState, useEffect, useRef } from 'react';
import {
    fetchCategoryIds, fetchCategoryName, fetchProductInfo,
    listFolderImages, uploadItemImage,
    addProduct, updateProduct, deleteProducts,
    addCategory, renameCategory, deleteCategoryAndContents, updateCategoryCount,
} from '../../bridge';
import UpgradeModal from './UpgradeModal';
import ManagedServiceBanner from './ManagedServiceBanner';

const FREE_CATEGORIES = 2;
const PAID_CATEGORIES = 9;
const FREE_PRODUCTS = 5;
const PAID_PRODUCTS = 20;

// ── Single product card ───────────────────────────────────────────────────────
const ProductCard = ({ product, currency, onSave, onDelete }) => {
    const [name, setName] = useState(product.product_name || '');
    const [price, setPrice] = useState(product.product_price || '');
    const [description, setDescription] = useState(product.product_description || '');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleSave = async () => { setSaving(true); await onSave(product.imageIndex, name, price, description); setSaving(false); };
    const handleDelete = async () => { setDeleting(true); await onDelete(product.imageIndex); };

    return (
        <div className="item-card">
            <img src={product.url} alt={name || 'Product'} className="item-card-image" />
            <div className="item-card-body">
                <input className="item-card-field" value={name} onChange={e => setName(e.target.value)} placeholder="Product name" />
                <input className="item-card-field" value={price} onChange={e => setPrice(e.target.value)} placeholder={`Price (${currency})`} />
                <textarea className="item-card-field textarea" value={description} onChange={e => setDescription(e.target.value)} placeholder="Description" />
                <div className="item-card-actions">
                    <button className="small-btn primary" onClick={handleSave} disabled={saving || deleting}>{saving ? '…' : 'Save'}</button>
                    <button className="small-btn danger" onClick={handleDelete} disabled={saving || deleting}>{deleting ? '…' : 'Delete'}</button>
                </div>
            </div>
        </div>
    );
};

// ── Single category card ──────────────────────────────────────────────────────
const CategoryCard = ({ category, email, currency, isSubscribed, onDelete }) => {
    const fileInputRef = useRef(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categoryName, setCategoryName] = useState(category.name);
    const [editingName, setEditingName] = useState(false);
    const [nameInput, setNameInput] = useState(category.name);
    const [nameSaving, setNameSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [showUpgrade, setShowUpgrade] = useState(false);

    useEffect(() => { loadProducts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadProducts = async () => {
        setLoading(true);
        const images = await listFolderImages(email, category.id);
        const withInfo = await Promise.all(images.map(async img => {
            const info = await fetchProductInfo({ userEmail: email, categoryId: category.id, imageIndex: img.name });
            return { imageIndex: img.name, url: img.url, product_name: info?.product_name || '', product_price: info?.product_price || '', product_description: info?.product_description || '' };
        }));
        setProducts(withInfo);
        setLoading(false);
    };

    const handleAddProduct = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        const filename = await uploadItemImage(email, category.id, file);
        if (filename) {
            await addProduct(email, category.id, filename, '', '', '');
            const url = `https://yeybluihiblysywmazen.supabase.co/storage/v1/object/public/images/${email}/${category.id}/${filename}`;
            setProducts(prev => [...prev, { imageIndex: filename, url, product_name: '', product_price: '', product_description: '' }]);
        }
        setUploading(false);
        e.target.value = '';
    };

    const handleAddProductClick = () => {
        const limit = isSubscribed ? PAID_PRODUCTS : FREE_PRODUCTS;
        if (products.length >= FREE_PRODUCTS && !isSubscribed) { setShowUpgrade(true); return; }
        if (products.length >= limit) return;
        fileInputRef.current.click();
    };

    const handleSaveProduct = async (imageIndex, name, price, description) => {
        await updateProduct(email, category.id, imageIndex, name, price, description);
        setProducts(prev => prev.map(p => p.imageIndex === imageIndex ? { ...p, product_name: name, product_price: price, product_description: description } : p));
    };

    const handleDeleteProduct = async (imageIndex) => {
        await deleteProducts(email, category.id, [imageIndex]);
        setProducts(prev => prev.filter(p => p.imageIndex !== imageIndex));
    };

    const handleSaveName = async () => {
        setNameSaving(true);
        await renameCategory(email, category.id, nameInput.trim() || categoryName);
        setCategoryName(nameInput.trim() || categoryName);
        setEditingName(false);
        setNameSaving(false);
    };

    const atProductLimit = products.length >= PAID_PRODUCTS;

    return (
        <>
            {showUpgrade && <UpgradeModal email={email} onDismiss={() => setShowUpgrade(false)} />}
            <div className="category-card">
                <div className="category-card-header">
                    {editingName ? (
                        <>
                            <input
                                className="category-name-input"
                                value={nameInput}
                                onChange={e => setNameInput(e.target.value)}
                                autoFocus
                                onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') { setEditingName(false); setNameInput(categoryName); } }}
                            />
                            <div className="category-header-actions">
                                <button className="hdr-icon-btn hdr-icon-btn-cancel" title="Cancel" onClick={() => { setEditingName(false); setNameInput(categoryName); }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                </button>
                                <button className="hdr-icon-btn hdr-icon-btn-save" title="Save" onClick={handleSaveName} disabled={nameSaving}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <span className="category-name-display">{categoryName}</span>
                            <div className="category-header-actions">
                                <span style={{ fontSize: 12, color: '#9C8677' }}>{products.length}/{isSubscribed ? PAID_PRODUCTS : FREE_PRODUCTS}</span>
                                <button className="hdr-icon-btn hdr-icon-btn-edit" title="Rename" onClick={() => setEditingName(true)}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                </button>
                                {deleteConfirm ? (
                                    <>
                                        <button className="small-btn danger" onClick={() => onDelete(category.id)}>Confirm</button>
                                        <button className="small-btn" onClick={() => setDeleteConfirm(false)}>Cancel</button>
                                    </>
                                ) : (
                                    <button className="hdr-icon-btn hdr-icon-btn-cancel" title="Delete category" onClick={() => setDeleteConfirm(true)}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className="category-body">
                    {loading ? <p className="manage-empty">Loading products…</p> : products.length === 0 ? <p className="manage-empty">No products yet. Add one below.</p> : (
                        <div className="items-grid">
                            {products.map(p => (
                                <ProductCard key={p.imageIndex} product={p} currency={currency} onSave={handleSaveProduct} onDelete={handleDeleteProduct} />
                            ))}
                        </div>
                    )}
                    <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAddProduct} />
                    <button
                        className="small-btn success"
                        onClick={handleAddProductClick}
                        disabled={uploading || atProductLimit}
                        title={atProductLimit ? `Maximum ${PAID_PRODUCTS} products reached` : ''}
                    >
                        {uploading ? 'Uploading…' : `+ Add product${!isSubscribed ? ` (${products.length}/${FREE_PRODUCTS} free)` : ''}`}
                    </button>
                </div>
            </div>
        </>
    );
};

// ── Main CategoryManager ──────────────────────────────────────────────────────
const CategoryManager = ({ email, currency, isSubscribed }) => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [adding, setAdding] = useState(false);
    const [showUpgrade, setShowUpgrade] = useState(false);

    useEffect(() => { loadCategories(); }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadCategories = async () => {
        setLoading(true);
        const ids = await fetchCategoryIds(email);
        if (!ids || ids.length === 0) { setCategories([]); setLoading(false); return; }
        const withNames = await Promise.all(ids.map(async ({ category_id }) => {
            const name = await fetchCategoryName({ userEmail: email, categoryId: category_id });
            return { id: category_id, name: name || `Category ${category_id}` };
        }));
        setCategories(withNames);
        setLoading(false);
    };

    const handleAddCategoryClick = () => {
        if (categories.length >= FREE_CATEGORIES && !isSubscribed) { setShowUpgrade(true); return; }
        if (categories.length >= PAID_CATEGORIES) return;
        setShowAddForm(true);
    };

    const handleAddCategory = async () => {
        const name = newCategoryName.trim();
        if (!name) return;
        setAdding(true);
        const newId = categories.length === 0 ? 1 : Math.max(...categories.map(c => c.id)) + 1;
        const ok = await addCategory(email, newId, name);
        if (ok) {
            await updateCategoryCount(email, categories.length + 1);
            setCategories(prev => [...prev, { id: newId, name }]);
        }
        setNewCategoryName('');
        setShowAddForm(false);
        setAdding(false);
    };

    const handleDeleteCategory = async (categoryId) => {
        await deleteCategoryAndContents(email, categoryId);
        const updated = categories.filter(c => c.id !== categoryId);
        setCategories(updated);
        await updateCategoryCount(email, updated.length);
    };

    const atCategoryLimit = categories.length >= PAID_CATEGORIES;
    const categoryLimit = isSubscribed ? PAID_CATEGORIES : FREE_CATEGORIES;

    if (loading) return <div className="manage-loading">Loading menu…</div>;

    return (
        <>
            {showUpgrade && <UpgradeModal email={email} onDismiss={() => setShowUpgrade(false)} />}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {categories.length === 0 && <p className="manage-empty">No categories yet. Add your first one below.</p>}

                {categories.map(cat => (
                    <CategoryCard
                        key={cat.id}
                        category={cat}
                        email={email}
                        currency={currency}
                        isSubscribed={isSubscribed}
                        onDelete={handleDeleteCategory}
                    />
                ))}

                {showAddForm ? (
                    <div className="manage-card">
                        <h2 className="manage-card-title">New category</h2>
                        <div className="add-category-form">
                            <input
                                value={newCategoryName}
                                onChange={e => setNewCategoryName(e.target.value)}
                                placeholder="Category name (e.g. Starters)"
                                autoFocus
                                onKeyDown={e => { if (e.key === 'Enter') handleAddCategory(); if (e.key === 'Escape') setShowAddForm(false); }}
                            />
                            <button className="small-btn primary" onClick={handleAddCategory} disabled={adding || !newCategoryName.trim()}>{adding ? 'Adding…' : 'Add'}</button>
                            <button className="small-btn" onClick={() => { setShowAddForm(false); setNewCategoryName(''); }}>Cancel</button>
                        </div>
                    </div>
                ) : (
                    <button
                        className="manage-add-btn"
                        onClick={handleAddCategoryClick}
                        disabled={atCategoryLimit}
                        title={atCategoryLimit ? `Maximum ${PAID_CATEGORIES} categories reached` : ''}
                    >
                        {`+ Add category${!isSubscribed ? ` (${categories.length}/${categoryLimit} free)` : ''}`}
                    </button>
                )}
                <ManagedServiceBanner email={email} />
            </div>
        </>
    );
};

export default CategoryManager;
